import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react'

interface HelpContextValue {
  isOpen: boolean
  articleId: string | null
  openArticle: (id: string) => void
  openHelp: () => void
  close: () => void
  /** Help article of the page on screen (set by PageHeader), so the
   *  sidebar Help Center link and the drawer footer deep-link to it. */
  pageDocId: string | null
  setPageDocId: (id: string | null) => void
}

const HelpContext = createContext<HelpContextValue>({
  isOpen: false,
  articleId: null,
  openArticle: () => {},
  openHelp: () => {},
  close: () => {},
  pageDocId: null,
  setPageDocId: () => {},
})

export function useHelp() {
  return useContext(HelpContext)
}

export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [articleId, setArticleId] = useState<string | null>(null)
  const [pageDocId, setPageDocId] = useState<string | null>(null)

  const openArticle = useCallback((id: string) => {
    setArticleId(id)
    setIsOpen(true)
  }, [])

  const openHelp = useCallback(() => {
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo(
    () => ({ isOpen, articleId, openArticle, openHelp, close, pageDocId, setPageDocId }),
    [isOpen, articleId, openArticle, openHelp, close, pageDocId],
  )

  return <HelpContext.Provider value={value}>{children}</HelpContext.Provider>
}
